import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@gokrew/ui';
import { TranslationNS } from '@gokrew/models/constants';
import type { DialogComponentProps } from '@gokrew/models/dialog';
import type { BillingSeatLimitDetails } from '@gokrew/models/billing';
import { useCloseDialog } from '@/hooks/performance/useCloseDialog';
import { formatMoney } from '@/utils/billing';
import styles from './BillingPaywallDialog.module.css';

interface BillingPaywallDialogProps extends DialogComponentProps {
  details: Partial<BillingSeatLimitDetails>;
  /** The viewer holds `billing.update` and can add a card. */
  canManageBilling: boolean;
  /**
   * Leaves for the Billing page. Passed in by the listener because this
   * content renders outside the router, where `useNavigate` is unavailable.
   */
  onAddPaymentMethod: () => void;
}

/**
 * The upgrade dialog the paywall opens when adding an employee would take the
 * company past its free tier. Billing admins go add a card; everyone else is
 * told who can.
 */
const BillingPaywallDialog = ({
  dialogId,
  details,
  canManageBilling,
  onAddPaymentMethod,
}: BillingPaywallDialogProps) => {
  const { t, i18n } = useTranslation(TranslationNS.Billing);
  const close = useCloseDialog(dialogId);
  const { freeLimit, activeEmployees, pricePerEmployee, currency } = details;
  const hasDetails =
    freeLimit !== undefined &&
    activeEmployees !== undefined &&
    pricePerEmployee !== undefined &&
    !!currency;

  return (
    <div className={styles.body}>
      <p className={styles.text}>
        {hasDetails
          ? t('paywall.body', {
              freeLimit,
              price: formatMoney(pricePerEmployee, currency, i18n.language),
              count: activeEmployees,
            })
          : t('paywall.body_generic')}
      </p>
      {!canManageBilling && (
        <p className={styles.hint}>{t('paywall.ask_owner')}</p>
      )}
      <div className={styles.footer}>
        {canManageBilling ? (
          <>
            <Button variant="outline" onClick={close}>
              {t('paywall.not_now')}
            </Button>
            <Button onClick={onAddPaymentMethod}>
              {t('payment_method.add')}
            </Button>
          </>
        ) : (
          <Button onClick={close}>{t('paywall.close')}</Button>
        )}
      </div>
    </div>
  );
};

export default memo(BillingPaywallDialog);
